import { Fragment, useCallback, useEffect, useMemo, useState } from "react";

/**
 * Cartera y vencimientos de clientes (solo lectura sobre SAP B1 / HANA).
 *  - Facturación por mes: por cliente (unificado por CIF entre sociedades),
 *    desglosable por empresa. Facturas menos abonos, sin anuladas.
 *  - Pendiente y vencido: partidas abiertas de cliente con tramos de antigüedad.
 * El acceso lo concede un admin por usuario (ver Users.tsx / requireCartera).
 */

type Company = { id: string; name: string; currency: string };
type Err = { company: string; error: string };
type BillingRow = {
  key: string; cif: string; cardCode: string; name: string; company: string;
  ym: string; date: string; type: string; doc: string; ref: string; base: number; total: number;
};
type OpenRow = {
  key: string; cif: string; cardCode: string; name: string; company: string;
  type: string; doc: string; ref: string; memo: string; payMethod: string;
  docDate: string; due: string; original: number; saldo: number;
};
type Billing = { rows: BillingRow[]; errors: Err[]; cachedAt: number };
type Open = { rows: OpenRow[]; errors: Err[]; cachedAt: number; today: string };

type Tab = "billing" | "open";
type AmountMode = "base" | "total";
type SortState = { key: string; dir: 1 | -1 };

/** Color propio de cada sociedad (chips, KPIs y cabecera del desglose). */
const COMPANY_COLORS: Record<string, string> = {
  AGRO: "#2e9e4f", EIS: "#e07b00", EMO: "#2d6fd6", EVT: "#8e44ad",
  GPA: "#d6336c", LAE: "#0f9aa8", TERMO: "#c0392b", ESA: "#7f8c1f", ESAI: "#6d4c41", MAROC: "#546e7a",
};
const coColor = (id: string): string => COMPANY_COLORS[id] ?? "#64748b";
const coStyle = (id: string): React.CSSProperties => ({ ["--co" as string]: coColor(id) });

const eur = new Intl.NumberFormat("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt = (v: number): string => (Math.abs(v) < 0.005 ? "" : eur.format(v));
const dmy = (iso: string): string => (iso ? iso.split("-").reverse().join("/") : "");
const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const monthLabel = (ym: string): string => {
  const [y, m] = ym.split("-");
  return `${MONTHS[Number(m) - 1]} ${(y ?? "").slice(2)}`;
};
const ymNow = (): string => new Date().toISOString().slice(0, 7);
function addMonths(ym: string, n: number): string {
  const [y, m] = ym.split("-").map(Number) as [number, number];
  const d = new Date(y, m - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function monthRange(from: string, to: string): string[] {
  const out: string[] = [];
  for (let m = from; m <= to && out.length < 60; m = addMonths(m, 1)) out.push(m);
  return out;
}
const daysBetween = (a: string, b: string): number => Math.round((+new Date(a) - +new Date(b)) / 86400000);

const BUCKETS = [
  { id: "nv", label: "No vencido", test: (d: number) => d <= 0, cls: "" },
  { id: "b1", label: "1-30 días", test: (d: number) => d >= 1 && d <= 30, cls: "cv-warn" },
  { id: "b2", label: "31-60 días", test: (d: number) => d >= 31 && d <= 60, cls: "cv-warn" },
  { id: "b3", label: "61-90 días", test: (d: number) => d >= 61 && d <= 90, cls: "cv-bad" },
  { id: "b4", label: "> 90 días", test: (d: number) => d > 90, cls: "cv-bad" },
] as const;
type BucketId = (typeof BUCKETS)[number]["id"];
const bucketOf = (days: number): BucketId => BUCKETS.find((b) => b.test(days))!.id;
const bucketCls = (days: number): string => BUCKETS.find((b) => b.test(days))!.cls;

async function api<T>(path: string, companies: string[], refresh: boolean): Promise<T> {
  const params = new URLSearchParams({ companies: companies.join(",") });
  if (refresh) params.set("refresh", "1");
  const res = await fetch(`/api/cartera${path}${path.includes("?") ? "&" : "?"}${params}`);
  const text = await res.text();
  let body: unknown = null;
  try { body = text ? JSON.parse(text) : null; } catch { /* keep null */ }
  if (!res.ok) {
    const msg = (body as { error?: string } | null)?.error;
    throw new Error(msg === "cartera_forbidden" ? "No tienes acceso a Cartera y vencimientos." : msg || `HTTP ${res.status}`);
  }
  return body as T;
}

/** true en pantallas de móvil: se cambia la tabla por tarjetas. */
function useIsMobile(): boolean {
  const q = "(max-width: 760px)";
  const [m, setM] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setM(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return m;
}

// ── ancho de columnas redimensionable (se recuerda en el navegador) ───────

const NAME_W = 340;
const NUM_W = 112;

function useColWidths(storeKey: string) {
  const [w, setW] = useState<Record<string, number>>(() => {
    try { return JSON.parse(localStorage.getItem(storeKey) ?? "{}") as Record<string, number>; } catch { return {}; }
  });
  const set = useCallback((id: string, width: number) => {
    setW((prev) => {
      const next = { ...prev, [id]: Math.max(50, Math.round(width)) };
      try { localStorage.setItem(storeKey, JSON.stringify(next)); } catch { /* sin almacenamiento */ }
      return next;
    });
  }, [storeKey]);
  const width = (id: string): number => w[id] ?? (id === "name" ? NAME_W : NUM_W);
  return { width, set };
}

type ColsApi = ReturnType<typeof useColWidths>;

/** <colgroup> + ancho total: con table-layout:fixed cada columna respeta su ancho. */
function tableSizing(ids: string[], cols: ColsApi): { group: JSX.Element; style: React.CSSProperties } {
  return {
    group: <colgroup>{ids.map((id) => <col key={id} style={{ width: cols.width(id) }} />)}</colgroup>,
    style: { width: ids.reduce((a, id) => a + cols.width(id), 0) },
  };
}

function Th({ id, cols, className = "", onSort, children }: {
  id: string; cols: ColsApi; className?: string; onSort: () => void; children: React.ReactNode;
}): JSX.Element {
  const startDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const x0 = e.clientX;
    const w0 = cols.width(id);
    const move = (ev: MouseEvent) => cols.set(id, w0 + ev.clientX - x0);
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      document.body.style.userSelect = "";
    };
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };
  return (
    <th className={`${className} cv-sortable`} onClick={onSort}>
      {children}
      <span className="cv-resizer" onMouseDown={startDrag} onClick={(e) => e.stopPropagation()} title="Arrastra para cambiar el ancho" />
    </th>
  );
}

function downloadCsv(rows: (string | number)[][], name: string): void {
  const body = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(";")).join("\r\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob(["﻿" + body], { type: "text/csv;charset=utf-8" }));
  a.download = `${name}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function Cartera(): JSX.Element {
  // Pantalla ancha: usa todo el ancho disponible (menos la barra lateral).
  useEffect(() => {
    document.body.classList.add("main-wide");
    return () => document.body.classList.remove("main-wide");
  }, []);
  const [tab, setTab] = useState<Tab>("billing");
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [from, setFrom] = useState(addMonths(ymNow(), -11));
  const [to, setTo] = useState(ymNow());
  const [mode, setMode] = useState<AmountMode>("base");
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");
  const isMobile = useIsMobile();
  const [showFilters, setShowFilters] = useState(false);
  const [billing, setBilling] = useState<Billing | null>(null);
  const [open, setOpen] = useState<Open | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<Record<Tab, SortState>>({
    billing: { key: "total", dir: -1 },
    open: { key: "total", dir: -1 },
  });

  useEffect(() => {
    api<Company[]>("/companies", [], false)
      .then((cs) => { setCompanies(cs); setSelected(new Set(cs.map((c) => c.id))); })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  const selectedKey = useMemo(() => [...selected].sort().join(","), [selected]);

  const load = useCallback(async (refresh = false) => {
    if (selected.size === 0) return;
    setLoading(true);
    setError(null);
    try {
      const ids = [...selected];
      if (tab === "billing") {
        if (!from || !to) return;
        setBilling(await api<Billing>(`/billing?from=${from}&to=${to}`, ids, refresh));
      } else {
        setOpen(await api<Open>("/open", ids, refresh));
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, from, to, selectedKey]);

  useEffect(() => { void load(); }, [load]);

  const q = search.trim().toLowerCase();
  const match = (r: { name: string; cif: string; cardCode: string }): boolean =>
    !q || r.name.toLowerCase().includes(q) || r.cif.toLowerCase().includes(q) || r.cardCode.toLowerCase().includes(q);

  const toggleExpanded = (k: string) =>
    setExpanded((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const toggleSort = (key: string) =>
    setSort((s) => ({ ...s, [tab]: { key, dir: s[tab].key === key ? (-s[tab].dir as 1 | -1) : -1 } }));
  const toggleCompany = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) { if (n.size > 1) n.delete(id); } else n.add(id);
      return n;
    });

  const data = tab === "billing" ? billing : open;
  const view = tab === "billing"
    ? <BillingTable data={billing} mode={mode} from={from} to={to} match={match} sort={sort.billing}
        expanded={expanded} onToggle={toggleExpanded} onSort={toggleSort} />
    : <OpenTable data={open} typeFilter={typeFilter} match={match} sort={sort.open}
        expanded={expanded} onToggle={toggleExpanded} onSort={toggleSort} />;

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>Cartera y vencimientos de clientes</h2>
          <p className="meta">SAP Business One · HANA · solo lectura. Acceso restringido a los usuarios designados por un administrador.</p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          {data && <span className="meta">Datos de {new Date(data.cachedAt).toLocaleTimeString("es-ES")}</span>}
          <button type="button" onClick={() => void load(true)} disabled={loading}
            title="Vuelve a consultar SAP ignorando la caché">
            {loading ? "Consultando…" : "Actualizar"}
          </button>
        </div>
      </div>

      <div className="cv-tabs">
        <button type="button" className={"cv-tab" + (tab === "billing" ? " active" : "")} onClick={() => setTab("billing")}>
          Facturación por mes
        </button>
        <button type="button" className={"cv-tab" + (tab === "open" ? " active" : "")} onClick={() => setTab("open")}>
          Pendiente y vencido
        </button>
      </div>

      {isMobile && (
        <button type="button" className="cv-filters-toggle" onClick={() => setShowFilters((v) => !v)}>
          {showFilters ? "Ocultar filtros" : "Filtros"} · {[...selected].join(", ")}
        </button>
      )}
      <div className={"cv-filters" + (isMobile && !showFilters ? " cv-hide" : "")}>
        <div className="cv-chips">
          {companies.map((c) => (
            <button key={c.id} type="button" title={c.name}
              style={coStyle(c.id)}
              className={"cv-chip" + (selected.has(c.id) ? " on" : "")} onClick={() => toggleCompany(c.id)}>
              {c.id}
            </button>
          ))}
        </div>
        <input type="search" placeholder="Buscar cliente o NIF/CIF…" value={search} onChange={(e) => setSearch(e.target.value)} />
        {tab === "billing" ? (
          <>
            <label>Desde <input type="month" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
            <label>Hasta <input type="month" value={to} onChange={(e) => setTo(e.target.value)} /></label>
            <select value={mode} onChange={(e) => setMode(e.target.value as AmountMode)}>
              <option value="base">Base imponible (sin IVA)</option>
              <option value="total">Total (con IVA)</option>
            </select>
          </>
        ) : (
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">Facturas y efectos</option>
            <option value="Factura,Abono">Solo facturas / abonos</option>
            <option value="Efecto">Solo efectos</option>
            <option value="all">Todo (incl. asientos y cobros)</option>
          </select>
        )}
      </div>

      {error && <div className="warning">Error: {error}</div>}
      {data?.errors.map((e) => (
        <div key={e.company} className="warning">Error en {e.company}: {e.error}</div>
      ))}
      {loading && !data && <p className="meta">Consultando SAP…</p>}
      {view}
    </section>
  );
}

// ── facturación por mes ───────────────────────────────────────────────────

type Ctl = {
  match: (r: { name: string; cif: string; cardCode: string }) => boolean;
  sort: SortState;
  expanded: Set<string>;
  onToggle: (k: string) => void;
  onSort: (k: string) => void;
};

function BillingTable(p: Ctl & { data: Billing | null; mode: AmountMode; from: string; to: string }): JSX.Element | null {
  const { data, mode, from, to, sort } = p;
  const cols = useColWidths("cartera.cols.billing");
  const isMobile = useIsMobile();
  const computed = useMemo(() => {
    if (!data) return null;
    const months = monthRange(from, to);
    type Co = { byMonth: Record<string, number>; total: number; docs: BillingRow[] };
    type Cl = { key: string; name: string; cif: string; byMonth: Record<string, number>; byCompany: Record<string, Co>; total: number };
    const clients = new Map<string, Cl>();
    const rows = data.rows.filter(p.match);
    for (const r of rows) {
      let c = clients.get(r.key);
      if (!c) { c = { key: r.key, name: r.name, cif: r.cif, byMonth: {}, byCompany: {}, total: 0 }; clients.set(r.key, c); }
      const v = r[mode];
      c.byMonth[r.ym] = (c.byMonth[r.ym] ?? 0) + v;
      c.total += v;
      const bc = (c.byCompany[r.company] ??= { byMonth: {}, total: 0, docs: [] });
      bc.docs.push(r);
      bc.byMonth[r.ym] = (bc.byMonth[r.ym] ?? 0) + v;
      bc.total += v;
    }
    const val = (c: Cl): number => (sort.key === "total" ? c.total : c.byMonth[sort.key] ?? 0);
    const list = [...clients.values()].sort((a, b) =>
      sort.key === "name" ? a.name.localeCompare(b.name) * -sort.dir : (val(a) - val(b)) * sort.dir);
    const colTot: Record<string, number> = Object.fromEntries(months.map((m) => [m, 0]));
    let grand = 0;
    for (const c of list) { for (const m of months) colTot[m]! += c.byMonth[m] ?? 0; grand += c.total; }
    const byCo: Record<string, number> = {};
    for (const r of rows) byCo[r.company] = (byCo[r.company] ?? 0) + r[mode];
    return { months, list, colTot, grand, byCo };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, mode, from, to, sort.key, sort.dir, p.match]);

  if (!computed) return null;
  const { months, list, colTot, grand, byCo } = computed;
  if (isMobile) {
    return (
      <>
        <div className="cv-kpis">
          <Kpi label="Facturación total" value={fmt(grand)} />
          {Object.entries(byCo).sort((a, b) => b[1] - a[1]).map(([co, v]) => <Kpi key={co} label={co} value={fmt(v)} co={co} />)}
        </div>
        <div className="cv-cards">
          {list.map((c) => {
            const k = "b" + c.key;
            const isOpen = p.expanded.has(k);
            return (
              <div key={c.key} className="cv-card">
                <div className="cv-card-head" onClick={() => p.onToggle(k)}>
                  <div className="cv-card-name"><span className="cv-caret">{isOpen ? "▾" : "▸"}</span>{c.name}</div>
                  <div className="cv-card-total">{fmt(c.total)}</div>
                </div>
                {isOpen && Object.entries(c.byCompany).map(([co, bc]) => (
                  <div key={co} className="cv-card-co" style={coStyle(co)}>
                    <div className="cv-card-cohead"><b>{co}</b><b>{fmt(bc.total)}</b></div>
                    <div className="cv-months">
                      {months.filter((m) => bc.byMonth[m]).map((m) => (
                        <span key={m} className="cv-month"><small>{monthLabel(m)}</small> {fmt(bc.byMonth[m] ?? 0)}</span>
                      ))}
                    </div>
                    {[...bc.docs].sort((a, b) => a.date.localeCompare(b.date)).map((r, i) => (
                      <div key={i} className="cv-doc">
                        <span>{r.type} {r.doc}<small> {dmy(r.date)}</small></span>
                        <span>{fmt(r[mode])}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            );
          })}
          <div className="cv-card cv-card-total-row"><span>TOTAL ({list.length} clientes)</span><b>{fmt(grand)}</b></div>
        </div>
      </>
    );
  }
  const cell = (v: number) => <td className={"n" + (v ? "" : " cv-zero")}>{v ? fmt(v) : "·"}</td>;
  const sizing = tableSizing(["name", ...months, "total"], cols);

  const exportCsv = () => {
    const out: (string | number)[][] = [["Cliente", "CIF", "Empresa", ...months, "Total"]];
    for (const c of list) {
      out.push([c.name, c.cif, "TODAS", ...months.map((m) => (c.byMonth[m] ?? 0).toFixed(2)), c.total.toFixed(2)]);
      for (const [co, bc] of Object.entries(c.byCompany)) {
        out.push([c.name, c.cif, co, ...months.map((m) => (bc.byMonth[m] ?? 0).toFixed(2)), bc.total.toFixed(2)]);
      }
    }
    downloadCsv(out, "facturacion");
  };

  return (
    <>
      <div className="cv-kpis">
        <Kpi label="Facturación total" value={fmt(grand)} />
        {Object.entries(byCo).sort((a, b) => b[1] - a[1]).map(([co, v]) => <Kpi key={co} label={co} value={fmt(v)} co={co} />)}
        <button type="button" onClick={exportCsv} style={{ marginLeft: "auto", alignSelf: "center" }}>Exportar CSV</button>
      </div>
      <div className="cv-wrap">
        <table className="data cv-table" style={sizing.style}>
          {sizing.group}
          <thead className="sticky-head">
            <tr>
              <Th id="name" cols={cols} onSort={() => p.onSort("name")}>Cliente</Th>
              {months.map((m) => <Th key={m} id={m} cols={cols} className="n" onSort={() => p.onSort(m)}>{monthLabel(m)}</Th>)}
              <Th id="total" cols={cols} className="n" onSort={() => p.onSort("total")}>Total</Th>
            </tr>
          </thead>
          <tbody>
            {list.map((c) => {
              const isOpen = p.expanded.has("b" + c.key);
              return (
                <Fragment key={c.key}>
                  <tr className="cv-client" onClick={() => p.onToggle("b" + c.key)}>
                    <td className="cv-name"><span className="cv-caret">{isOpen ? "▾" : "▸"}</span>{c.name}</td>
                    {months.map((m) => <Fragment key={m}>{cell(c.byMonth[m] ?? 0)}</Fragment>)}
                    <td className="n"><b>{fmt(c.total)}</b></td>
                  </tr>
                  {isOpen && Object.entries(c.byCompany).map(([co, bc]) => (
                    <Fragment key={co}>
                      <tr className="cv-detail cv-co" style={coStyle(co)}>
                        <td className="cv-name cv-ind1"><b>{co}</b></td>
                        {months.map((m) => <Fragment key={m}>{cell(bc.byMonth[m] ?? 0)}</Fragment>)}
                        <td className="n"><b>{fmt(bc.total)}</b></td>
                      </tr>
                      {[...bc.docs].sort((a, b) => a.date.localeCompare(b.date)).map((r, i) => (
                        <tr key={i} className="cv-detail">
                          <td className="cv-name cv-ind2">{r.type} {r.doc}{r.ref ? ` · ${r.ref}` : ""} <small>{dmy(r.date)}</small></td>
                          {months.map((m) => (m === r.ym ? <Fragment key={m}>{cell(r[mode])}</Fragment> : <td key={m}></td>))}
                          <td className="n">{fmt(r[mode])}</td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="cv-total">
              <td>TOTAL ({list.length} clientes)</td>
              {months.map((m) => <td key={m} className="n">{fmt(colTot[m] ?? 0)}</td>)}
              <td className="n">{fmt(grand)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}

// ── pendiente y vencido ───────────────────────────────────────────────────

type Tot = Record<BucketId | "total", number>;
const emptyTot = (): Tot => ({ nv: 0, b1: 0, b2: 0, b3: 0, b4: 0, total: 0 });

function OpenTable(p: Ctl & { data: Open | null; typeFilter: string }): JSX.Element | null {
  const { data, typeFilter, sort } = p;
  const cols = useColWidths("cartera.cols.open");
  const isMobile = useIsMobile();
  const computed = useMemo(() => {
    if (!data) return null;
    const types = typeFilter === "all" ? null : (typeFilter || "Factura,Abono,Efecto").split(",");
    const items = data.rows
      .filter((r) => p.match(r) && (!types || types.includes(r.type)))
      .map((r) => ({ ...r, days: daysBetween(data.today, r.due) }));
    type It = (typeof items)[number];
    type Co = Tot & { items: It[] };
    type Cl = Tot & { key: string; name: string; cif: string; items: It[]; byCompany: Record<string, Co> };
    const clients = new Map<string, Cl>();
    for (const r of items) {
      let c = clients.get(r.key);
      if (!c) { c = { key: r.key, name: r.name, cif: r.cif, items: [], byCompany: {}, ...emptyTot() }; clients.set(r.key, c); }
      c[bucketOf(r.days)] += r.saldo;
      c.total += r.saldo;
      c.items.push(r);
      const bc = (c.byCompany[r.company] ??= { items: [], ...emptyTot() });
      bc[bucketOf(r.days)] += r.saldo;
      bc.total += r.saldo;
      bc.items.push(r);
    }
    const list = [...clients.values()].sort((a, b) =>
      sort.key === "name"
        ? a.name.localeCompare(b.name) * -sort.dir
        : ((a[sort.key as keyof Tot] ?? 0) - (b[sort.key as keyof Tot] ?? 0)) * sort.dir);
    const tot = emptyTot();
    for (const c of list) (Object.keys(tot) as (keyof Tot)[]).forEach((k) => { tot[k] += c[k]; });
    const efectos = items.filter((r) => r.type === "Efecto").reduce((s, r) => s + r.saldo, 0);
    return { list, tot, efectos };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, typeFilter, sort.key, sort.dir, p.match]);

  if (!computed || !data) return null;
  const { list, tot, efectos } = computed;
  const vencido = tot.b1 + tot.b2 + tot.b3 + tot.b4;
  const pct = tot.total ? Math.round((vencido / tot.total) * 100) : 0;
  const sizing = tableSizing(["name", ...BUCKETS.map((b) => b.id), "total"], cols);
  if (isMobile) {
    return (
      <>
        <div className="cv-kpis">
          <Kpi label="Total pendiente" value={fmt(tot.total)} />
          <Kpi label="Pendiente de vencer" value={fmt(tot.nv)} cls="cv-good" />
          <Kpi label={`Vencido (${pct}%)`} value={fmt(vencido)} cls="cv-bad" />
          <Kpi label="De ello, efectos" value={fmt(efectos)} />
        </div>
        <div className="cv-cards">
          {list.map((c) => {
            const k = "o" + c.key;
            const isOpen = p.expanded.has(k);
            const venc = c.b1 + c.b2 + c.b3 + c.b4;
            return (
              <div key={c.key} className="cv-card">
                <div className="cv-card-head" onClick={() => p.onToggle(k)}>
                  <div className="cv-card-name"><span className="cv-caret">{isOpen ? "▾" : "▸"}</span>{c.name}</div>
                  <div className="cv-card-total">{fmt(c.total)}</div>
                </div>
                <div className="cv-buckets">
                  {BUCKETS.filter((b) => c[b.id]).map((b) => (
                    <span key={b.id} className={"cv-bucket " + b.cls}><small>{b.label}</small> {fmt(c[b.id])}</span>
                  ))}
                  {venc !== 0 && <span className="cv-bucket cv-bad"><small>Vencido</small> <b>{fmt(venc)}</b></span>}
                </div>
                {isOpen && Object.entries(c.byCompany).map(([co, bc]) => (
                  <div key={co} className="cv-card-co" style={coStyle(co)}>
                    <div className="cv-card-cohead"><b>{co}</b><b>{fmt(bc.total)}</b></div>
                    {[...bc.items].sort((a, b) => a.due.localeCompare(b.due)).map((r, i) => (
                      <div key={i} className="cv-doc">
                        <span>
                          {r.type} {r.doc}{r.payMethod && <span className="cv-tag"> {r.payMethod}</span>}
                          <br />
                          <small>Vto. {dmy(r.due)}{" "}
                            {r.days > 0
                              ? <span className="cv-tag o">vencido {r.days} d</span>
                              : <span className="cv-tag">vence en {-r.days} d</span>}
                          </small>
                        </span>
                        <span className={bucketCls(r.days)}>{fmt(r.saldo)}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            );
          })}
          <div className="cv-card cv-card-total-row"><span>TOTAL ({list.length} clientes)</span><b>{fmt(tot.total)}</b></div>
        </div>
      </>
    );
  }
  const cell = (v: number, cls = "") => <td className={"n " + (v ? cls : "cv-zero")}>{v ? fmt(v) : "·"}</td>;

  const exportCsv = () => {
    const out: (string | number)[][] = [["Cliente", "CIF", "Empresa", "Tipo", "Documento", "Fecha", "Vencimiento", "Días vencido", "Forma de pago", "Saldo pendiente"]];
    for (const c of list) {
      for (const r of [...c.items].sort((a, b) => a.due.localeCompare(b.due))) {
        out.push([c.name, c.cif, r.company, r.type, r.doc, r.docDate, r.due, Math.max(r.days, 0), r.payMethod, r.saldo.toFixed(2)]);
      }
    }
    downloadCsv(out, "cartera");
  };

  return (
    <>
      <div className="cv-kpis">
        <Kpi label="Total pendiente" value={fmt(tot.total)} />
        <Kpi label="Pendiente de vencer" value={fmt(tot.nv)} cls="cv-good" />
        <Kpi label={`Vencido (${pct}%)`} value={fmt(vencido)} cls="cv-bad" />
        <Kpi label="De ello, efectos en cartera" value={fmt(efectos)} />
        <button type="button" onClick={exportCsv} style={{ marginLeft: "auto", alignSelf: "center" }}>Exportar CSV</button>
      </div>
      <div className="cv-wrap">
        <table className="data cv-table" style={sizing.style}>
          {sizing.group}
          <thead className="sticky-head">
            <tr>
              <Th id="name" cols={cols} onSort={() => p.onSort("name")}>Cliente</Th>
              {BUCKETS.map((b) => <Th key={b.id} id={b.id} cols={cols} className="n" onSort={() => p.onSort(b.id)}>{b.label}</Th>)}
              <Th id="total" cols={cols} className="n" onSort={() => p.onSort("total")}>Total pendiente</Th>
            </tr>
          </thead>
          <tbody>
            {list.map((c) => {
              const isOpen = p.expanded.has("o" + c.key);
              return (
                <Fragment key={c.key}>
                  <tr className="cv-client" onClick={() => p.onToggle("o" + c.key)}>
                    <td className="cv-name"><span className="cv-caret">{isOpen ? "▾" : "▸"}</span>{c.name}</td>
                    {BUCKETS.map((b) => <Fragment key={b.id}>{cell(c[b.id], b.cls)}</Fragment>)}
                    <td className="n"><b>{fmt(c.total)}</b></td>
                  </tr>
                  {isOpen && Object.entries(c.byCompany).map(([co, bc]) => (
                    <Fragment key={co}>
                      <tr className="cv-detail cv-co" style={coStyle(co)}>
                        <td className="cv-name cv-ind1"><b>{co}</b></td>
                        {BUCKETS.map((b) => <Fragment key={b.id}>{cell(bc[b.id], b.cls)}</Fragment>)}
                        <td className="n"><b>{fmt(bc.total)}</b></td>
                      </tr>
                      {[...bc.items].sort((a, b) => a.due.localeCompare(b.due)).map((r, i) => (
                        <tr key={i} className="cv-detail">
                          <td className="cv-name cv-ind2">
                            {r.type} {r.doc}{r.ref ? ` · ${r.ref}` : ""}
                            {r.payMethod && <span className="cv-tag"> {r.payMethod}</span>}
                            <br />
                            <small>
                              {dmy(r.docDate)} → Vto. {dmy(r.due)}{" "}
                              {r.days > 0
                                ? <span className="cv-tag o">vencido {r.days} d</span>
                                : <span className="cv-tag">vence en {-r.days} d</span>}
                            </small>
                          </td>
                          {BUCKETS.map((b) => (bucketOf(r.days) === b.id
                            ? <Fragment key={b.id}>{cell(r.saldo, b.cls)}</Fragment>
                            : <td key={b.id}></td>))}
                          <td className="n">{fmt(r.saldo)}</td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="cv-total">
              <td>TOTAL ({list.length} clientes)</td>
              {BUCKETS.map((b) => <td key={b.id} className="n">{fmt(tot[b.id])}</td>)}
              <td className="n">{fmt(tot.total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}

function Kpi({ label, value, cls = "", co }: { label: string; value: string; cls?: string; co?: string }): JSX.Element {
  return (
    <div className={"cv-kpi" + (co ? " cv-kpi-co" : "")} style={co ? coStyle(co) : undefined}>
      <div className="cv-kpi-l">{label}</div>
      <div className={"cv-kpi-v " + cls}>{value || "0,00"}</div>
    </div>
  );
}
